import { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, CheckCircle2, XCircle, AlertCircle, TrendingUp, TrendingDown, CalendarDays } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const fmt = (n) => (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_META = {
  paid: { label: 'Paid', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  partial: { label: 'Partial', cls: 'bg-amber-100 text-amber-700 border-amber-200', Icon: AlertCircle },
  unpaid: { label: 'Unpaid', cls: 'bg-rose-100 text-rose-700 border-rose-200', Icon: XCircle },
  overpaid: { label: 'Overpaid', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200', Icon: AlertCircle },
  'n/a': { label: '—', cls: 'bg-muted text-muted-foreground border-border', Icon: CalendarDays },
};

/**
 * Per-person salary/payment status — full calendar year (Jan–Dec) with a year
 * selector and a rolling-12-months toggle. Works for both employees and
 * partners. Reuses the /salary-report (employee_id) and
 * /partner-payment-report (partner_id) endpoints.
 */
export function SalaryStatusDialog({ open, onOpenChange, entity }) {
  // entity: { id, name, type: 'employee' | 'partner' }
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [rolling, setRolling] = useState(false);
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!entity?.id) return;
    setLoading(true);
    try {
      let fromM, toM;
      if (rolling) {
        const f = new Date(now.getFullYear(), now.getMonth() - 11, 1);
        fromM = `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}`;
        toM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      } else {
        fromM = `${year}-01`;
        toM = `${year}-12`;
      }
      const path = entity.type === 'partner' ? '/partner-payment-report' : '/salary-report';
      const params = entity.type === 'partner'
        ? { from_month: fromM, to_month: toM, partner_id: entity.id }
        : { from_month: fromM, to_month: toM, employee_id: entity.id };
      const { data } = await api.get(path, { params });
      setGroup((data.groups && data.groups[0]) || null);
    } catch { toast.error('Failed to load salary status'); } finally { setLoading(false); }
  }, [entity, year, rolling]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (open) load(); }, [open, load]);

  const t = group?.totals;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="salary-status-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <CalendarDays size={18} /> Salary Status — {entity?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-1.5">
            <Button size="icon" variant="outline" className="h-8 w-8" disabled={rolling}
                    onClick={() => setYear(y => y - 1)} data-testid="salary-status-prev-year"><ChevronLeft size={15} /></Button>
            <span className="font-semibold text-sm w-28 text-center" data-testid="salary-status-year">
              {rolling ? 'Last 12 months' : year}
            </span>
            <Button size="icon" variant="outline" className="h-8 w-8" disabled={rolling || year >= now.getFullYear()}
                    onClick={() => setYear(y => y + 1)} data-testid="salary-status-next-year"><ChevronRight size={15} /></Button>
          </div>
          <Button size="sm" variant={rolling ? 'default' : 'outline'} className="h-8 rounded-full text-[12px]"
                  onClick={() => setRolling(r => !r)} data-testid="salary-status-rolling-toggle">
            {rolling ? 'Calendar year' : 'Rolling 12 months'}
          </Button>
        </div>

        {t && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="salary-status-totals">
            <div className="rounded-lg border p-2"><div className="text-[10px] text-muted-foreground">Expected</div><div className="font-bold text-sm">{fmt(t.due)}</div></div>
            <div className="rounded-lg border p-2"><div className="text-[10px] text-muted-foreground">Paid</div><div className="font-bold text-sm text-emerald-600">{fmt(t.paid)}</div></div>
            <div className="rounded-lg border p-2"><div className="text-[10px] text-muted-foreground">Outstanding</div><div className="font-bold text-sm text-rose-600">{fmt(t.outstanding)}</div></div>
            <div className="rounded-lg border p-2"><div className="text-[10px] text-muted-foreground">Overpaid</div><div className="font-bold text-sm text-indigo-600">{fmt(t.overpaid)}</div></div>
          </div>
        )}

        <div className="max-h-[55dvh] overflow-y-auto rounded-lg border">
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-muted/40 sticky top-0">
                <tr className="text-left text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Month</th>
                  <th className="px-2 py-2 font-medium text-center">Status</th>
                  <th className="px-2 py-2 font-medium text-right">Salary</th>
                  <th className="px-2 py-2 font-medium text-right">Paid</th>
                  <th className="px-2 py-2 font-medium text-right">Outstanding</th>
                  <th className="px-2 py-2 font-medium text-right">Over</th>
                  <th className="px-3 py-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground" data-testid="salary-status-loading">Loading…</td></tr>}
                {!loading && !group && <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground" data-testid="salary-status-empty">No data.</td></tr>}
                {!loading && group?.months.map((m) => {
                  const meta = STATUS_META[m.status] || STATUS_META['n/a'];
                  const { Icon } = meta;
                  return (
                    <tr key={m.month} className="border-t border-border/50" data-testid={`salary-status-month-${m.month}`}>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        {m.month_label}
                        {m.salary_changed && (
                          <span className="ml-1 inline-flex items-center text-[10px] text-indigo-600" title={`Salary ${m.salary_change?.direction}: ${fmt(m.salary_change?.from)} → ${fmt(m.salary_change?.to)}`}>
                            {m.salary_change?.direction === 'decrease' ? <TrendingDown size={11} /> : <TrendingUp size={11} />}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${meta.cls}`}>
                          <Icon size={11} />{meta.label}
                        </span>
                        {m.duplicate && <span className="ml-1 text-[10px] text-rose-600" title={`${m.payment_count} payments this month`}>×{m.payment_count}</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right">{fmt(m.monthly_salary)}</td>
                      <td className="px-2 py-1.5 text-right">{fmt(m.paid_amount)}</td>
                      <td className="px-2 py-1.5 text-right text-rose-600">{m.outstanding ? fmt(m.outstanding) : '—'}</td>
                      <td className="px-2 py-1.5 text-right text-indigo-600">{m.overpaid ? fmt(m.overpaid) : '—'}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">{m.payment_date || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
